import type { DailyGoalHistoryDay } from "@consistency-circle/shared";

import { addDays } from "../lib/goal-stats";

const DAY_LABELS = ["", "M", "", "W", "", "F", ""];
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

type DayCell = {
  readonly localDate: string;
  readonly day: DailyGoalHistoryDay | null;
  /** Filler before the range starts; rendered invisible to keep columns aligned. */
  readonly pad?: boolean;
};

/** Weeks as columns, Sunday-first rows, oldest week on the left, ending at `todayDate`. */
function buildWeeks(days: DailyGoalHistoryDay[], todayDate: string): DayCell[][] {
  const byDate = new Map(days.map((day) => [day.localDate, day]));
  const start = days[0]?.localDate ?? todayDate;
  const startDow = new Date(`${start}T12:00:00Z`).getUTCDay();

  const weeks: DayCell[][] = [];
  let week: DayCell[] = Array.from({ length: startDow }, (_, i) => ({
    localDate: addDays(start, i - startDow),
    day: null,
    pad: true,
  }));

  for (let date = start; date <= todayDate; date = addDays(date, 1)) {
    week.push({ localDate: date, day: byDate.get(date) ?? null });
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) weeks.push(week);
  return weeks;
}

function cellClass(day: DailyGoalHistoryDay | null): string {
  if (!day || (day.totalCount === 0 && day.skippedCount === 0)) return "bg-muted-foreground/15";
  if (day.totalCount === 0) return "bg-primary/15"; // fully skipped: a rest day, not a gap
  if (day.completionPercent >= 80) return "bg-primary";
  if (day.completionPercent >= 40) return "bg-primary/55";
  if (day.completionPercent > 0) return "bg-primary/25";
  return "bg-muted-foreground/15";
}

function cellTitle(cell: DayCell): string {
  if (!cell.day || (cell.day.totalCount === 0 && cell.day.skippedCount === 0)) {
    return `${cell.localDate}: nothing tracked`;
  }
  if (cell.day.totalCount === 0) return `${cell.localDate}: rest day`;
  return `${cell.localDate}: ${cell.day.completionPercent}%`;
}

export function CalendarHeatmap({
  days,
  todayDate,
}: {
  days: DailyGoalHistoryDay[];
  todayDate: string;
}) {
  const weeks = buildWeeks(days, todayDate);

  // Label a month at the first week that contains its 1st.
  const monthLabels = weeks.map((week) => {
    const firstOfMonth = week.find((cell) => cell.localDate.endsWith("-01"));
    if (!firstOfMonth) return "";
    return MONTH_NAMES[Number(firstOfMonth.localDate.slice(5, 7)) - 1] ?? "";
  });

  return (
    <div className="space-y-1.5 overflow-x-auto">
      <div className="flex gap-[3px]">
        <div className="w-4 shrink-0" />
        {monthLabels.map((label, index) => (
          <span
            key={index}
            className="w-[13px] shrink-0 overflow-visible whitespace-nowrap text-[9px] text-muted-foreground"
          >
            {label}
          </span>
        ))}
      </div>

      <div className="flex gap-[3px]">
        <div className="flex w-4 shrink-0 flex-col justify-between">
          {DAY_LABELS.map((label, index) => (
            <span key={index} className="h-[13px] text-[9px] leading-[13px] text-muted-foreground">
              {label}
            </span>
          ))}
        </div>
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="flex shrink-0 flex-col gap-[3px]">
            {week.map((cell) => (
              <div
                key={cell.localDate}
                title={cell.pad ? undefined : cellTitle(cell)}
                className={`h-[13px] w-[13px] rounded-[3px] ${
                  cell.pad ? "bg-transparent" : cellClass(cell.day)
                } ${cell.localDate === todayDate ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : ""}`}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1.5 pt-0.5 text-[10px] text-muted-foreground">
        less
        <span className="h-[13px] w-[13px] rounded-[3px] bg-muted-foreground/15" />
        <span className="h-[13px] w-[13px] rounded-[3px] bg-primary/25" />
        <span className="h-[13px] w-[13px] rounded-[3px] bg-primary/55" />
        <span className="h-[13px] w-[13px] rounded-[3px] bg-primary" />
        more
        <span className="ml-2.5 h-[13px] w-[13px] rounded-[3px] bg-primary/15" />
        rest day
      </div>
    </div>
  );
}
